import { create } from 'zustand';
import api from '@/utils/api';
import { Curriculum, CurriculumPlan, PlanSection } from '@/lib/types';
import { unwrap } from '@/pages/planner/api';

interface CreateCurriculumPayload {
  workspace_id: number;
  name: string;
  description: string;
  // Curriculum-wide, authored at creation (curCreate step 1) and editable later
  // via curDetails — not per slot. Day plans copied from a slot snapshot these.
  learning_outcomes: string;
  prerequisites: string;
  plan_count: number;
  // The shared plan shape set in the wizard's structure step. The backend
  // copies both onto every slot it seeds (regenerating section uids per slot
  // and dropping items — content is added per plan).
  duration_minutes?: number;
  sections?: PlanSection[];
}

interface CurriculumsStore {
  items: Curriculum[];
  error: string | null;
  isLoading: boolean;
  load: (workspaceId: string) => Promise<void>;
  fetchOne: (id: number) => Promise<Curriculum>;
  create: (payload: CreateCurriculumPayload) => Promise<Curriculum>;
  update: (id: number, payload: Partial<Curriculum>) => Promise<Curriculum>;
  remove: (id: number) => Promise<void>;
  saveSlot: (slotId: number, payload: Partial<CurriculumPlan>) => Promise<CurriculumPlan>;
}

const useCurriculumsStore = create<CurriculumsStore>()((set) => ({
  items: [],
  error: null,
  isLoading: false,
  load: async (workspaceId) => {
    try {
      set({ isLoading: true, error: null });
      const response = await api.get(`curriculums/workspace/${workspaceId}`);
      const curriculums = unwrap<Curriculum[]>(response) ?? [];
      set({ items: curriculums, isLoading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.error || 'Failed to load curriculums',
        isLoading: false,
      });
    }
  },
  // Fetches a single curriculum with its plans[] (including is_built/sections)
  // and merges it into `items` — used by curBuild/curPlans to get fresh slot
  // data on mount and again after a slot save, so Built/Empty chips update
  // without needing a full workspace-wide reload.
  fetchOne: async (id) => {
    try {
      set({ error: null });
      const response = await api.get(`curriculums/${id}`);
      const fetched = unwrap<Curriculum>(response);
      set((state) => {
        const exists = state.items.some((item) => item.id === id);
        return {
          items: exists
            ? state.items.map((item) => (item.id === id ? fetched : item))
            : [...state.items, fetched],
        };
      });
      return fetched;
    } catch (error: any) {
      set({ error: error.response?.data?.error || 'Failed to load curriculum' });
      throw error;
    }
  },
  create: async (payload) => {
    try {
      set({ error: null });
      const response = await api.post('curriculums', payload);
      const created = unwrap<Curriculum>(response);
      set((state) => ({ items: [...state.items, created] }));
      return created;
    } catch (error: any) {
      set({ error: error.response?.data?.error || 'Failed to create curriculum' });
      throw error;
    }
  },
  update: async (id, payload) => {
    try {
      set({ error: null });
      const response = await api.put(`curriculums/${id}`, payload);
      const updated = unwrap<Curriculum>(response);
      set((state) => ({
        items: state.items.map((item) => (item.id === id ? updated : item)),
      }));
      return updated;
    } catch (error: any) {
      set({ error: error.response?.data?.error || 'Failed to update curriculum' });
      throw error;
    }
  },
  remove: async (id) => {
    try {
      set({ error: null });
      await api.delete(`curriculums/${id}`);
      set((state) => ({ items: state.items.filter((item) => item.id !== id) }));
    } catch (error: any) {
      set({ error: error.response?.data?.error || 'Failed to delete curriculum' });
      throw error;
    }
  },
  saveSlot: async (slotId, payload) => {
    try {
      set({ error: null });
      const response = await api.put(`curriculums/plans/${slotId}`, payload);
      const updated = unwrap<CurriculumPlan>(response);
      set((state) => ({
        items: state.items.map((curriculum) =>
          curriculum.plans
            ? {
                ...curriculum,
                plans: curriculum.plans.map((plan) => (plan.id === slotId ? updated : plan)),
              }
            : curriculum,
        ),
      }));
      return updated;
    } catch (error: any) {
      set({ error: error.response?.data?.error || 'Failed to save curriculum slot' });
      throw error;
    }
  },
}));

export default useCurriculumsStore;
